import React, { PureComponent } from 'react'
import Table from "../../../../components/AgGrid/Table";
import ColumnSumHeader from "../../../../components/AgGrid/ColumnSumHeader";
import {
    Board,
    FileCell,
    TrashCell,
    AutoSizer,
    BooleanCell,
    StatusTag,
    HighlightCell
} from '../../../../components/DataTable'
import {htmlDecode} from '../../../../utils/utils'
import { connect } from 'react-redux'
import { toastr } from 'react-redux-toastr'
import _ from 'lodash'
import {
    fetchFormObject,
    deleteObject,
    applyAction,
    fetchDtData,
    exportTableObject,
    saveFormObject,
    customFetchFormObject,
} from '../../actions/api'
import {executeLoadSubscriptions, executeDtLoadSubscriptions} from '../../actions/module'
import {
    sortDataTable,
    setFiltersOpened,
    filterDataTable,
    setColumnSelectorOpened,
    setActionSelectorOpened,
    toggleColumnVisibility,
    setObjectMode,
    setEditedRowId,
    setListElementSelection,
    setFieldWidth,
    setDataTableFilterModel,
    setDataTableSort
} from '../../actions'
import {
    getFiltersExist,
    getDataTableColumns,
    getSortBy,
    getSortDirection,
    getDataTableFilter,
    getDataTableFilterModel,
    getFiltersOpened,
    getEditFieldsLength,
    getColumnSelectorOptions,
    getColumnSelectorOpened,
    getActionSelectorOpened,
    getDataTableSortedObjects,
    getLanguage,
    getEditedRowId,
    getUser, getLastEditDate,
    getDataLists,
    getListAccessId
} from '../../selectors'
import './KpDataTable.css'
import {SquarePlay, FileText, Image} from "lucide-react";
import {LOCALE_MAPPING} from "../Form/Number";
import {translateName} from "../../../../utils/i18n";
import ObjectArrayEditor from "../../../../components/AgGrid/editors/ObjectArrayEditor";
import ObjectEditor from "../../../../components/AgGrid/editors/ObjectEditor";
import PhoneNumberEditor from "../../../../components/AgGrid/editors/PhoneNumberEditor";
import getFormValidations from "../Form/formValidations";

export const getFormattedAmount = (amount, numberOfDecimals, language) => {
    return amount == null ? '---' :
        new Intl.NumberFormat(LOCALE_MAPPING[language], {
            minimumFractionDigits: numberOfDecimals,
            maximumFractionDigits: numberOfDecimals
        }).format(amount)
}

const getCellSearchText = (column, row, language, t) => {
    const translate = raw => {
        if (raw == null) return ''
        if (column.translateName) return translateName(raw, language) || ''
        if (column.translate) return t(raw, { capitalize: true })
        return String(raw)
    }
    switch (column.type) {
        case 'text':
        case 'textarea':
        case 'date':
        case 'dateString':
            return translate(column.dataGetter(row))
        case 'object': {
            const v = column.dataGetter(row)
            if (!v) return ''
            return translate(column.display ? _.get(v, column.display) : v)
        }
        case 'objectArray':
        case 'tags': {
            const values = column.dataGetter(row)
            if (!_.isArray(values)) return ''
            return values.map(o => translate(_.get(o, column.display))).join(', ')
        }
        case 'number':
            return getFormattedAmount(column.dataGetter(row), column.numberOfDecimals, language)
        case 'creatableTags': {
            const v = column.dataGetter(row)
            return _.isArray(v) ? v.join(' ') : ''
        }
        default:
            return ''
    }
}

const countOccurrencesInString = (str, lowerSearch) => {
    if (!str) return 0
    const lowerStr = str.toLowerCase()
    let count = 0
    let cursor = 0
    let idx = lowerStr.indexOf(lowerSearch, cursor)
    while (idx !== -1) {
        count += 1
        cursor = idx + lowerSearch.length
        idx = lowerStr.indexOf(lowerSearch, cursor)
    }
    return count
}

const countMatchedOccurrences = (objects, columns, searchText, language, t) => {
    if (!searchText || !_.isArray(objects)) return 0
    const lower = String(searchText).toLowerCase()
    const visibleColumns = columns.filter(c => !c.hidden)
    return objects.reduce((count, row) => {
        const rowCount = visibleColumns.reduce((acc, col) => {
            const str = getCellSearchText(col, row, language, t)
            return acc + countOccurrencesInString(str, lower)
        }, 0)
        return count + rowCount
    }, 0)
}

const getCellEditor = (column) => {
    switch (column.type) {
        case "textarea":
            return "agLargeTextCellEditor"
        case "object":
            return ObjectEditor
        case "objectArray":
            return ObjectArrayEditor
        case "phoneNumber":
            return PhoneNumberEditor
        default:
            return undefined
    }
}

function cellRenderer(field, user, language, groupModel, accessId, searchText) {
    switch (field.type) {
        case 'boolean':
            return ({ value }) => <BooleanCell value={value} />
        case 'status':
            return ({ value }) => <StatusTag value={value.value} bStyle={value.bStyle} style={value.style} />
        case 'icon' :
            return ({ value }) => {
                const handledTypes = ['videoIcon', 'imageIcon', 'documentIcon']
                if(!value || !handledTypes.includes(value)) return null

                if (value === 'videoIcon') return <SquarePlay size={16}/>
                if (value === 'imageIcon') return <Image size={16}/>
                if (value === 'documentIcon') return <FileText size={16}/>
                return null
            }
        case 'videoIcon':
            return ({ value }) => {
                if(!value) return null

                return <SquarePlay size={16}/>
            }
        case 'creatableTags':
            return ({value}) => {
                return (
                    <div style={{display: 'flex', flexDirection: 'row'}}>
                        {value && value.map(v => (
                            <div style={{
                                padding: '3px',
                                margin: '3px',
                                borderRadius: '4px',
                                background: '#addbff91',
                                border: '1px solid #96bedc'
                            }}>
                                <HighlightCell value={v} searchText={searchText} />
                            </div>
                        ))}
                    </div>
                )
            }
        case 'documentIcon':
            return ({ value }) => {
                if(!value) return null

                return <FileText size={16}/>
            }
        case 'number':
            return ({ value }) => {
                return (
                    <div style={{textAlign: 'end', paddingRight: '10px'}}>
                        <HighlightCell
                            value={getFormattedAmount(value, field.numberOfDecimals, language)}
                            searchText={searchText}
                        />
                    </div>
                )
            }
        case 'fileLink':
            return ({data}) => {
                const cellData = _.get(data, field.path)
                return cellData
                    ? <FileCell file={cellData} language={language} groupModel={groupModel} accessId={accessId} />
                    : null
            }
        case 'text':
        case 'textarea':
        case 'object':
        case 'objectArray':
        case 'tags':
        case 'date':
        case 'dateString':
            return (p) => <HighlightCell value={p.valueFormatted ?? p.value} searchText={searchText} />
        default:
            return null
    }
}

const COLUMN_WIDTHS_STORAGE_PREFIX = 'kp-dt-column-widths-'

class KpDataTable extends PureComponent {
    enhanceColumnsCache = { inputs: null, result: null }
    matchedCountCache = { inputs: null, result: 0 }

    state = {
        deletingIds: [],
        displayedRowCount: null
    }

    handleDisplayedRowCountChanged = (count) => {
        this.setState({ displayedRowCount: count })
    }

    setDeleting = (id, isDeleting) => {
        this.setState(prevState => ({
            deletingIds: isDeleting
                ? [...prevState.deletingIds, id]
                : prevState.deletingIds.filter(dId => dId !== id)
        }))
    }

    componentDidMount() {
        this.props.executeDtLoadSubscriptions()
    }

    getColumnWidthsStorageKey() {
        return `${COLUMN_WIDTHS_STORAGE_PREFIX}${this.props.module.id}`
    }

    getSavedColumnWidths() {
        try {
            const raw = localStorage.getItem(this.getColumnWidthsStorageKey())
            return raw ? JSON.parse(raw) : {}
        } catch (e) {
            return {}
        }
    }

    onColumnResized = (event) => {
        if (!event.finished || !event.columns) return
        // Only persist widths from user-initiated resizes. Automatic sources
        // (flex, sizeColumnsToFit, autosizeColumns, gridInitializing, ...) would
        // otherwise freeze columns at their computed width and kill flex behavior.
        if (event.source !== 'uiColumnResized' && event.source !== 'uiColumnDragged') return
        try {
            const saved = this.getSavedColumnWidths()
            event.columns.forEach(col => {
                const colId = col.getColId()
                const width = col.getActualWidth()
                if (colId && width) saved[colId] = width
            })
            localStorage.setItem(this.getColumnWidthsStorageKey(), JSON.stringify(saved))
        } catch (e) {
            // ignore storage errors (quota, disabled, etc.)
        }
    }

    getEnhancedColumns(columns) {
        const { t, user, language, groupModel, accessId, sortBy, sortDirection, dataTableFilter } = this.props
        const inputs = { columns, t, user, language, groupModel, accessId, sortBy, sortDirection, dataTableFilter }
        const prev = this.enhanceColumnsCache.inputs
        if (prev && Object.keys(inputs).every(k => prev[k] === inputs[k])) {
            return this.enhanceColumnsCache.result
        }
        const result = this.enhanceColumns(columns)
        this.enhanceColumnsCache = { inputs, result }
        return result
    }

    getMatchedCount(objects, columns, filter) {
        const { language, t } = this.props
        const inputs = { objects, columns, filter, language, t }
        const prev = this.matchedCountCache.inputs
        if (prev && Object.keys(inputs).every(k => prev[k] === inputs[k])) {
            return this.matchedCountCache.result
        }
        const result = countMatchedOccurrences(objects, columns, filter, language, t)
        this.matchedCountCache = { inputs, result }
        return result
    }

    enhanceColumns(columns) {
        const { t, user, language, groupModel, accessId, sortBy, sortDirection, dataTableFilter } = this.props
        const filteredColumns = columns.filter(column => !column.hidden)
        const savedWidths = this.getSavedColumnWidths()

        return filteredColumns.map(column => {
            const editionProps = column.editable ? {
                cellEditor: getCellEditor(column),
                cellEditorParams: this.getCellEditorParams(column),
                cellEditorPopup: ["objectArray", "object", "textarea", "phoneNumber"].includes(column.type) || undefined,
            } : {}
            const renderer = cellRenderer(column, user, language, groupModel, accessId, dataTableFilter)
            const savedWidth = savedWidths[column.path]
            return {
                ...column,
                colId: column.path,
                dataKey: column.path,
                headerName: t(column.label || column.tKey),
                cellDataType: column.type,
                minWidth: column.width,
                ...(savedWidth ? { width: savedWidth } : { flex: column.flexGrow }),
                filter: !['fileLink', 'icon'].includes(column.type),
                sortable: true,
                editable: column.editable,
                sort: sortBy === column.path ? sortDirection.toLowerCase() : undefined, // Added for default sorting
                valueGetter: (p) => column.dataGetter(p.data),
                ...(column.type === 'number' && {
                    tooltipValueGetter: (p) => p.value == null ? undefined : getFormattedAmount(p.value, column.numberOfDecimals, language)
                }),
                ...(column.type === 'number' && column.tooltip && {
                    headerComponent: ColumnSumHeader,
                    headerComponentParams: {
                        t,
                        getFormattedAmount,
                        language,
                        numberOfDecimals: column.numberOfDecimals,
                        dataGetter: column.dataGetter
                    }
                }),
                ...(column.type === 'status' && {
                    filterValueGetter: (p) => {
                        const v = column.dataGetter(p.data)
                        return v && v.value
                    }
                }),
                ...(column.type === 'object' && (() => {
                    const objectLabel = v => {
                        if (!v) return ''
                        const raw = column.display ? _.get(v, column.display) : v
                        if (column.translateName) return translateName(raw, language)
                        if (column.translate) return t(raw, { capitalize: true })
                        return raw
                    }
                    return {
                        filterValueGetter: (p) => objectLabel(column.dataGetter(p.data)),
                        // sort on the displayed label, not on the raw object
                        comparator: (a, b) => String(objectLabel(a) || '').localeCompare(String(objectLabel(b) || ''), 'fr', { sensitivity: 'base', numeric: true })
                    }
                })()),
                ...((column.type === 'objectArray' || column.type === 'tags') && {
                    filterValueGetter: (p) => {
                        const values = column.dataGetter(p.data)
                        if (!_.isArray(values)) return ''
                        return values
                            .map(o => {
                                const raw = _.get(o, column.display)
                                if (column.translateName) return translateName(raw, language)
                                if (column.translate) return t(raw, { capitalize: true })
                                return raw
                            })
                            .filter(Boolean)
                            .join(', ')
                    }
                }),
                valueSetter: (params) => {
                    const { newValue, data } = params
                    const validations = getFormValidations(column, t, columns, data)
                    for (const validate of validations) {
                        const error = validate(newValue, data, { t })
                        if (error) {
                            setTimeout(() => {
                                toastr.error(t('validationError'), t(error))
                            }, 0)
                            return false
                        }
                    }

                    params.data[params.colDef.dataKey] = params.newValue; // update rowData
                    return true
                },
                valueFormatter: this.getCellFormatter(column),
                ...(renderer && { cellRenderer: renderer }),
                ...editionProps
            }
        })
    }

    getCellFormatter(column){
        const {language, t} = this.props

        const getTranslation = rawValue => {
            if(column.translateName) return translateName(rawValue, language)
            if(column.translate) return  t(rawValue, { capitalize: true })
            return rawValue
        }
        switch (column.type) {
            case "object":
                return p => {
                    return column.display
                        ? p.value && getTranslation(_.get(p.value, column.display))
                        : p.value
                }
            case "objectArray":
            case "tags":
                return p => {
                    const values = p.value

                    return values && _.isArray(values) &&
                        values.map(object => {
                            const display = _.get(object, column.display)
                            return getTranslation(display)
                        }).join(', ')
                }
                /*
            case "number":
                return p => getFormattedAmount(p.value, column.numberOfDecimals, language)
                 */
            case "text":
            case "textarea":
                return p => getTranslation(p.value)
            default:
                return undefined
        }
    }

    getCellEditorParams(column) {
        const { language } = this.props
        switch (column.type) {
            case "textarea":
                return {
                    maxLength: 999,
                    rows: 5,
                    cols: 100
                }
            case "object":
            case "objectArray":
                return {
                    values: this.getDataList(column.dataList),
                    displayKey: column.display,
                    width: column.width,
                    t: this.props.t
                }
            case "phoneNumber":
                return {
                    width: column.width,
                    language
                }
            default:
                return undefined
        }
    }

    getDataList(id) {
        const {dataLists} = this.props
        const dataList = dataLists.byId[id]

        return dataList.list.allIds.map(id => dataList.list.byId[id])
    }

    getDeleteColumn(deletionConditions) {
        return {
            id: 'l_deleteButton',
            dataKey: 'id',
            headerName: '',
            width: 50,
            sortable: false,
            resizable: false,
            editable: false,
            lockPosition: 'right',
            cellRenderer: (p) => {
                if(p.data.noDeleteButtonAccess) {
                    return null
                }
                const isDeleting = this.state.deletingIds.includes(p.data.id)
                return deletionConditions
                    ? Object.keys(deletionConditions).some(key => deletionConditions[key].includes(p.data[key]))
                        ? <TrashCell loading={isDeleting} onClick={() => this.props.onDeleteClick(p.data, this.setDeleting) } />
                        : null
                    : <TrashCell loading={isDeleting} onClick={ () => this.props.onDeleteClick(p.data, this.setDeleting) } />
            }
        }
    }

    onRowSelected = (event) => {
        const { onSelectCheckBoxClick } = this.props
        const { id } = event.data
        const isSelected = event.node.isSelected()

        onSelectCheckBoxClick(id, isSelected)
    }

    onRowClick = (event) => {
        /*
         */
        const target = event.event?.target;
        // ignore clicks inside the TrashCell or FileCell
        if (target?.closest('.DataTable-TrashCell') || target?.closest('.DataTable-FileCell')) {
            return;
        }

        const { editFields, setObjectMode, onLineClick } = this.props

        if (!editFields) return
        if (event.data.noFormAccess) return

        setObjectMode('editObject')
        onLineClick(event.data)
    }

    render() {
        const {
            columns,
            module,
            objects,
            //sortDataTable,
            t,
            user,
            filtersExist,
            filtersOpened,
            setFiltersOpened,
            dataTableFilter,
            filterDataTable,
            actionSelectorOpened,
            setActionSelectorOpened,
            columnSelectorOptions,
            columnSelectorOpened,
            setColumnSelectorOpened,
            toggleColumnVisibility,
            onActionClick,
            exportTableObject,
            language,
            editedRowId,
            setEditedRowId,
            onChange,
            setRowSaving,
            getInitialFilterModel,
            setDataTableSort,
            setDataTableFilterModel,
        } = this.props

        const changeFiltersVisibility = filtersExist
            ? () => setFiltersOpened(!filtersOpened)
            : undefined

        const enhancedColumns = this.getEnhancedColumns(columns)
        const tableColumns = _.compact([
            ...enhancedColumns,
            module.removable ? this.getDeleteColumn(module.deletionConditions) : undefined
        ])

        const matchedCount = this.getMatchedCount(objects, columns, dataTableFilter)

        const enableSelection = module.actions?.length > 0
        return (
            <div className="KpDataTable">
                <Board
                    actions={module.actions}
                    noLeftBoard={module.noLeftBoard}
                    useSocketsOnGet={module.useSocketsOnGet}
                    useSocketsOnFind={module.useSocketsOnFind}
                    actionSelectorOpened={actionSelectorOpened}
                    openActionSelector={setActionSelectorOpened}
                    selectorOptions={columnSelectorOptions.map(option => ({
                        ...option,
                        label: t(option.tKey)
                    }))}
                    columnSelectorOpened={columnSelectorOpened}
                    openColumnSelector={setColumnSelectorOpened}
                    changeColumnSelection={toggleColumnVisibility}
                    objects={objects}
                    matchedCount={matchedCount}
                    displayedRowCount={this.state.displayedRowCount}
                    columns={enhancedColumns}
                    filter={dataTableFilter}
                    changeFilter={filterDataTable}
                    filtersOpened={filtersOpened}
                    changeFiltersVisibility={changeFiltersVisibility}
                    searchTooltip={t(
                        objects.length > 1
                            ? 'elementTooltip_plural'
                            : 'elementTooltip',
                        {
                            count: objects.length
                        }
                    )}
                    searchPlaceholder={`${t('search')}...`}
                    moduleName={t(module.tKey)}
                    onActionClick={onActionClick}
                    exportTableObject={exportTableObject}
                    moduleId={module.id}
                    protectedExport={module.protectedExport}
                    user={user}
                    t={t}
                    language={language}
                />
                <div className="KpDataTable-Table">
                    <AutoSizer>
                        {({ height, width }) => (
                            <Table
                                height={height}
                                width={width}
                                objects={objects}
                                columns={tableColumns}
                                enableSelection={enableSelection}
                                onRowSelected={enableSelection ? this.onRowSelected : undefined}
                                onRowClick={this.onRowClick}
                                editedRowId={editedRowId}
                                setEditedRowId={setEditedRowId}
                                onChange={onChange}
                                setRowSaving={setRowSaving}
                                getInitialFilterModel={getInitialFilterModel}
                                onSortChanged={setDataTableSort}
                                onFilterChanged={setDataTableFilterModel}
                                onDisplayedRowCountChanged={this.handleDisplayedRowCountChanged}
                                onColumnResized={this.onColumnResized}
                                t={t}
                            />
                        )}
                    </AutoSizer>
                </div>
            </div>
        )
    }
}

const mapStateToProps = state => ({
    columns: getDataTableColumns(state),
    dataTableFilter: getDataTableFilter(state),
    filtersExist: getFiltersExist(state),
    filtersOpened: getFiltersOpened(state),
    objects: getDataTableSortedObjects(state),
    user: getUser(state),
    sortBy: getSortBy(state),
    sortDirection: getSortDirection(state),
    language: getLanguage(state),
    accessId: getListAccessId(state),
    editedRowId: getEditedRowId(state),
    lastEditDate: getLastEditDate(state),
    editFields: getEditFieldsLength(state),
    dataLists: getDataLists(state),
    columnSelectorOptions: getColumnSelectorOptions(state),
    columnSelectorOpened: getColumnSelectorOpened(state),
    actionSelectorOpened: getActionSelectorOpened(state),
})

const mapDispatchToProps = (dispatch, {module, groupModel, t }) => {
    return {
        setFieldWidth: (fieldId, width) => dispatch(setFieldWidth(fieldId, width)),
        setObjectMode: object => dispatch(setObjectMode(object)),
        setEditedRowId: id => {
            dispatch(setEditedRowId(id))
            setTimeout(() => {
                dispatch(setEditedRowId(null))
            }, 3000)
        },
        filterDataTable: value => dispatch(filterDataTable(value)),
        setFiltersOpened: opened => dispatch(setFiltersOpened(opened)),
        exportTableObject: (filename, columns, objects, user, protectedExport, language) =>
            dispatch(exportTableObject(filename, columns, objects, user, protectedExport, language, groupModel)),
        setColumnSelectorOpened: ({ opened }) => {
            dispatch(setColumnSelectorOpened(opened, groupModel))
        },
        setActionSelectorOpened: ({ opened }) =>
            dispatch(setActionSelectorOpened(opened)),
        toggleColumnVisibility: ({ columnId }) =>
            dispatch(toggleColumnVisibility(columnId)),
        sortDataTable: ({ sortBy, sortDirection }) =>
            dispatch(sortDataTable(sortBy, sortDirection)),
        setDataTableFilterModel: filterModel =>
            dispatch(setDataTableFilterModel(filterModel)),
        getInitialFilterModel: () =>
            dispatch((_, getState) => getDataTableFilterModel(getState())),
        setDataTableSort: ({ sortBy, sortDirection }) =>
            dispatch(setDataTableSort({ sortBy, sortDirection })),
        executeDtLoadSubscriptions: () => dispatch(executeDtLoadSubscriptions()),
        onLineClick: object => {
            dispatch(fetchFormObject(object.id))
                .then(({ error }) => {
                    if(!error) dispatch(executeLoadSubscriptions())
                })
        },
        onDeleteClick: (object, setDeleting) => {
            console.log('onDeleteClick')
            toastr.confirm(t(module.deletionConfirmationMessage || 'deletionConfirmation'), {
                onOk: () => {
                    setDeleting(object.id, true)
                    dispatch(deleteObject(object.id)).then(({ error }) => {
                        setDeleting(object.id, false)
                        if(error) {
                            const decodedError = htmlDecode(error)
                            toastr.error(t('deletion'), t(decodedError))
                        }
                        else toastr.success(t('deletion'), t('deletionSuccess', {number: 1}))
                    })
                }
            })
        },
        onChange: ({object}) => {
            return dispatch(customFetchFormObject(object.id)).then(({ error, data }) => {
                if(!error) {
                    return dispatch(
                        saveFormObject({
                            ...data,
                            ...object
                        }, { action: 'save' })
                    )
                        .then(({ error }) => ({ error }))
                        .catch(() => ({ error: 'Server error' }))
                }
            })
        },
        onSelectCheckBoxClick: (id, isChecked) => dispatch(setListElementSelection(id, isChecked)),
        onActionClick: (action, moduleId) => {
            toastr.confirm(t('actionConfirmation', {action: t(action.tKey)}), {
                onOk: () => {
                    dispatch(applyAction(action)).then((results) => {
                            const failed = results.filter(({error}) => error)
                            const succeed = results.filter(({error}) => !error)

                        const messageByMethod = {"PUT": "action", "DELETE": "deletion"}
                        const descriptionByMethod = {"PUT": {success: "actionSuccess", failure: "actionFailed"}, "DELETE": {success: "deletionSuccess", failure: "deletionFailure"}}

                            if(succeed.length) {
                                dispatch(fetchDtData(moduleId))
                                toastr.success(t(messageByMethod[action.method]), t(descriptionByMethod[action.method].success, {number: succeed.length}))
                            }

                            if(failed.length) {
                                toastr.error(t(messageByMethod[action.method]), t(descriptionByMethod[action.method].failure, {number: failed.length, errors: []}))
                            }
                        }
                    )
                }
            })
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(KpDataTable)
